import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'

import { api } from '@/lib/api-client'
import { toast } from 'sonner'
import { useOrganizationShop } from '@/hooks/use-organization-shop'
import { Button } from '@/components/ui/button'
import { Card,CardContent,CardHeader } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { AlertCircle,X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatInStoreTz } from '@/lib/time'

interface ReconciliationRecord {
  id: string
  packageLabel?: string
  itemName?: string
  localQuantity?: number | string
  metrcQuantity?: number | string
  delta?: number | string
  detectedAt?: string
  createdAt?: string
  rawData?: {
    PackageLabel?: string
    ItemName?: string
    LocalQuantity?: number
    MetrcQuantity?: number
    Delta?: number
  }
}

interface ReconciliationSidePanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  record: ReconciliationRecord | null
}

export function ReconciliationSidePanel({ open, onOpenChange, record }: ReconciliationSidePanelProps) {
  const [note, setNote] = useState('')
  const { shopId } = useOrganizationShop()

  // Hooks must run before the `!record || !open` early return (rules-of-hooks:
  // the hook count may not change between renders). `record` is therefore
  // nullable here; the mutations only fire from buttons rendered when it isn't.
  const resolveMutation = useMutation({
    mutationFn: () =>
      api.put(`/metrc/reconciliation/${record?.id}/resolve`, {
        shopId,
        note: note.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success('Resolved', { description: 'Discrepancy marked as resolved.' })
      onOpenChange(false)
    },
    onError: (err: unknown) =>
      toast.error(
        err instanceof Error ? err.message : 'Could not resolve discrepancy',
        { description: 'Resolve failed' },
      ),
  })

  const ignoreMutation = useMutation({
    mutationFn: () =>
      api.put(`/metrc/reconciliation/${record?.id}/ignore`, {
        shopId,
        note: note.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success('Ignored', { description: 'Discrepancy marked as ignored.' })
      onOpenChange(false)
    },
    onError: (err: unknown) =>
      toast.error(
        err instanceof Error ? err.message : 'Could not ignore discrepancy',
        { description: 'Ignore failed' },
      ),
  })

  const isBusy = resolveMutation.isPending || ignoreMutation.isPending

  if (!record || !open) return null

  const label = record.packageLabel ?? record.rawData?.PackageLabel ?? '—'
  const itemName = record.itemName ?? record.rawData?.ItemName ?? 'Unknown'
  const localQty = record.localQuantity ?? record.rawData?.LocalQuantity ?? '—'
  const metrcQty = record.metrcQuantity ?? record.rawData?.MetrcQuantity ?? '—'
  const delta = record.delta ?? record.rawData?.Delta ?? '—'
  const detectedAt = record.detectedAt ?? record.createdAt
  const detectedLabel = detectedAt ? formatInStoreTz(detectedAt, 'MMM dd, yyyy, hh:mm a') : 'N/A'
  const deltaValue = Number(delta)
  const deltaLabel = Number.isNaN(deltaValue) ? String(delta) : String(deltaValue)

  return (
    <Card className="w-full h-full border shadow-lg rounded-xl flex flex-col overflow-hidden animate-in slide-in-from-right-10 fade-in duration-300 relative bg-card">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-4 bg-background z-10 border-b relative shrink-0 pt-6">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold tracking-tight">Discrepancy: {label}</h2>
          </div>
          <div className="text-sm text-muted-foreground">{itemName}</div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onOpenChange(false)}
          className="transition-transform duration-200"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </Button>
      </CardHeader>

      <CardContent className="p-6 space-y-6 overflow-y-auto bg-background/50">
        <section className="space-y-3">
          <h3 className="text-xs font-semibold text-muted-foreground tracking-wider uppercase">QUANTITY COMPARISON</h3>
          <div className="bg-muted/40 p-4 rounded-lg border space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Local:</span>
              <span className="font-mono">{String(localQty)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Metrc:</span>
              <span className="font-mono">{String(metrcQty)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Delta:</span>
              <span className={cn('font-mono', deltaValue < 0 ? 'text-red-500' : 'text-emerald-600')}>{deltaLabel}</span>
            </div>
          </div>
        </section>

        <Separator />

        <section className="space-y-3">
          <h3 className="text-xs font-semibold text-muted-foreground tracking-wider uppercase">INVESTIGATION</h3>
          <div className="text-sm text-muted-foreground space-y-1">
            <div>Detected: {detectedLabel}</div>
            <div>Detected during: Post-sync reconciliation</div>
            <div>Auto-correct: NOT applied</div>
            <div className="text-xs text-muted-foreground">(Delta exceeds threshold of 0.01)</div>
          </div>
          <div className="text-sm text-muted-foreground">
            Possible causes:
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>Adjustment made directly in Metrc UI</li>
              <li>Sale reported to Metrc but not in POS</li>
              <li>Sync timing overlap</li>
            </ul>
          </div>
        </section>

        <Separator />

        <section className="space-y-3">
          <h3 className="text-xs font-semibold text-muted-foreground tracking-wider uppercase">RESOLUTION</h3>
          <div className="rounded-lg border bg-card/80 p-4 space-y-3">
            <Button variant="outline" className="w-full" onClick={() => ignoreMutation.mutate()} disabled={isBusy}>
              Accept Metrc Quantity
            </Button>
            <div className="text-xs text-muted-foreground">Sets local to Metrc value.</div>
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground">Resolution note</label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="min-h-[80px]"
                placeholder="Add a resolution note..."
              />
            </div>
            <Button className="w-full" onClick={() => resolveMutation.mutate()} disabled={isBusy}>
              Resolve
            </Button>
          </div>
        </section>

        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-lg p-3 flex gap-2 text-sm text-amber-800 dark:text-amber-500">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <p>Resolution actions are logged for audit history.</p>
        </div>
      </CardContent>
    </Card>
  )
}
